import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, Typography, TextField, Grid, 
    LinearProgress, Breadcrumbs, Link, Stack, 
    Select, MenuItem, FormControl, InputLabel, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import { Home, Add, Delete } from '@mui/icons-material';
import { BUTTON_CANCEL_COLOR, BUTTON_SAVE_COLOR } from '../../../common/color';
import Swal from 'sweetalert2';
import dealerApi from '../../../../services/dealerApi';

const DealerBody = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [dzongkhags, setDzongkhags] = useState([]);
    const [selectedDzongkhag, setSelectedDzongkhag] = useState('');
    const [dealers, setDealers] = useState([]);
    const [currentDealer, setCurrentDealer] = useState({
        name: '',
        address: '',
        phone: '',
        email: '',
        contactPerson: '',
    });
    const [errors, setErrors] = useState({
        dzongkhag: '',
        name: '',
        address: '',
        phone: '',
        email: '',
        contactPerson: ''
    });

    useEffect(() => {
        const fetchDzongkhags = async () => {
            setLoading(true);
            try {
                const response = await dealerApi.getDzongkhags();
                setDzongkhags(response);
            } catch (error) {
                console.error('Error fetching dzongkhags:', error);
                Swal.fire('Error', 'Failed to load dzongkhags', 'error');
            } finally {
                setLoading(false);
            }
        };

        fetchDzongkhags();
    }, []);

    const validateDealer = (dealer) => {
        const newErrors = {
            name: dealer.name.trim() ? '' : 'Name is required',
            address: dealer.address.trim() ? '' : 'Address is required',
            phone: dealer.phone.trim() ? '' : 'Phone is required',
            email: dealer.email.trim() ? 
                (/^\S+@\S+\.\S+$/.test(dealer.email) ? '' : 'Invalid email format') : 
                'Email is required',
            contactPerson: dealer.contactPerson.trim() ? '' : 'Contact person is required'
        };
        setErrors(newErrors);
        return !Object.values(newErrors).some(error => error !== '');
    };

    const handleAddDealer = () => {
        if (!validateDealer(currentDealer)) return;

        setDealers(prev => [...prev, {
            ...currentDealer,
            id: Date.now() // Temporary ID for local management
        }]);

        // Reset current dealer form
        setCurrentDealer({
            name: '',
            address: '',
            phone: '',
            email: '',
            contactPerson: '',
        });
    };

    const handleRemoveDealer = (id) => {
        setDealers(prev => prev.filter(dealer => dealer.id !== id));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCurrentDealer(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        if (!selectedDzongkhag) {
            Swal.fire('Error', 'Please select a dzongkhag', 'error');
            return;
        }

        if (dealers.length === 0) {
            Swal.fire('Error', 'Please add at least one dealer', 'error');
            return;
        }

        const confirm = await Swal.fire({
            title: 'Confirm',
            text: `Are you sure you want to add ${dealers.length} dealer(s) to ${dzongkhags.find(d => d.id === selectedDzongkhag)?.name}?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: BUTTON_SAVE_COLOR,
            cancelButtonColor: BUTTON_CANCEL_COLOR
        });

        if (!confirm.isConfirmed) return;

        try {
            setLoading(true);
            
            // Prepare dealers data (remove temporary IDs)
            const dealersToSubmit = dealers.map(dealer => {
                const { id, ...dealerData } = dealer;
                return dealerData;
            });

            // Call bulk create API
            await dealerApi.bulkCreate(selectedDzongkhag, dealersToSubmit);

            Swal.fire('Success', 'Dealers added successfully', 'success');
            navigate('/admin/dealers');
        } catch (error) {
            console.error('Error:', error);
            Swal.fire('Error', error.message || 'Operation failed', 'error');
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <LinearProgress />;

    return (
        <Box sx={{ width: '100%', p: 3 }}>
            <Dialog open fullWidth maxWidth="md" onClose={() => navigate('/admin/dealers')}>
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between">
                        <Breadcrumbs>
                            <Link href="/admin" underline="hover" color="inherit">
                                <Home sx={{ mr: 0.5 }} /> Master Management
                            </Link>
                            <Link href="/admin/dealers" underline="hover" color="inherit">
                                Dealers
                            </Link>
                            <Typography color="text.primary">
                                Add Dealers
                            </Typography>
                        </Breadcrumbs>
                    </Stack>
                </DialogTitle>

                <Divider />

                <DialogContent sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography variant="h5" gutterBottom>Select Dzongkhag</Typography>
                            <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                                <InputLabel>Dzongkhag</InputLabel>
                                <Select
                                    value={selectedDzongkhag}
                                    onChange={(e) => setSelectedDzongkhag(e.target.value)}
                                    label="Dzongkhag"
                                >
                                    {dzongkhags.map((dzongkhag) => (
                                        <MenuItem key={dzongkhag.id} value={dzongkhag.id}>
                                            {dzongkhag.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {selectedDzongkhag && (
                            <>
                                <Grid item xs={12}>
                                    <Typography variant="h5" gutterBottom>Add Dealers to {dzongkhags.find(d => d.id === selectedDzongkhag)?.name}</Typography>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        required
                                        fullWidth
                                        label="Dealer Name"
                                        name="name"
                                        value={currentDealer.name}
                                        onChange={handleInputChange}
                                        error={Boolean(errors.name)}
                                        helperText={errors.name}
                                        sx={{ mb: 2 }}
                                        size="small"
                                    />

                                    <TextField
                                        required
                                        fullWidth
                                        label="Address"
                                        name="address"
                                        value={currentDealer.address}
                                        onChange={handleInputChange}
                                        error={Boolean(errors.address)}
                                        helperText={errors.address}
                                        sx={{ mb: 2 }}
                                        size="small"
                                    />

                                    <TextField
                                        required
                                        fullWidth
                                        label="Phone"
                                        name="phone"
                                        value={currentDealer.phone}
                                        onChange={handleInputChange}
                                        error={Boolean(errors.phone)}
                                        helperText={errors.phone}
                                        sx={{ mb: 2 }}
                                        size="small"
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        required
                                        fullWidth
                                        label="Email"
                                        name="email"
                                        value={currentDealer.email}
                                        onChange={handleInputChange}
                                        error={Boolean(errors.email)}
                                        helperText={errors.email}
                                        sx={{ mb: 2 }}
                                        size="small"
                                    />

                                    <TextField
                                        required
                                        fullWidth
                                        label="Contact Person"
                                        name="contactPerson"
                                        value={currentDealer.contactPerson}
                                        onChange={handleInputChange}
                                        error={Boolean(errors.contactPerson)}
                                        helperText={errors.contactPerson}
                                        sx={{ mb: 2 }}
                                        size="small"
                                    />
                                    <Button
                                        variant="contained"
                                        startIcon={<Add />}
                                        onClick={handleAddDealer}
                                        sx={{ mt: 1 }}
                                    >
                                        Add Dealer
                                    </Button>
                                </Grid>

                                {dealers.length > 0 && (
                                    <Grid item xs={12}>
                                        <Typography variant="h6" gutterBottom>
                                            Dealers to be Added ({dealers.length})
                                        </Typography>
                                        <TableContainer component={Paper}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Name</TableCell>
                                                        <TableCell>Contact Person</TableCell>
                                                        <TableCell>Phone</TableCell>
                                                        <TableCell>Email</TableCell>
                                                        <TableCell>Action</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {dealers.map((dealer) => (
                                                        <TableRow key={dealer.id}>
                                                            <TableCell>{dealer.name}</TableCell>
                                                            <TableCell>{dealer.contactPerson}</TableCell>
                                                            <TableCell>{dealer.phone}</TableCell>
                                                            <TableCell>{dealer.email}</TableCell>
                                                            <TableCell>
                                                                <IconButton 
                                                                    onClick={() => handleRemoveDealer(dealer.id)}
                                                                    color="error"
                                                                >
                                                                    <Delete fontSize="small" />
                                                                </IconButton>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Grid>
                                )}
                            </>
                        )}
                    </Grid>
                </DialogContent>

                <Divider />

                <DialogActions sx={{ p: 3 }}>
                    <Button
                        variant="contained"
                        onClick={() => navigate('/admin/dealers')}
                        sx={{ background: BUTTON_CANCEL_COLOR }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={!selectedDzongkhag || dealers.length === 0 || loading}
                        sx={{ background: BUTTON_SAVE_COLOR }}
                    >
                        {loading ? 'Saving...' : 'Save All Dealers'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DealerBody;